import type { Collection } from '@/app/actions/collection';
import type { User } from '@/types/user/type';
import type { SportType } from '~/model/SportType';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { ApiRoute } from '@/app/utils/request/api-routes';
import { AcceptLanguage } from '~/const/locale';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { isGuestUser, isMainUser } from '../utils/entities/user';

export function useUserSports(child?: User) {
  const locale = useLocale();
  const [userSports, setUserSports] = useState<Collection<SportType>>();

  const { user, setUser } = useAuthenticatedContext();

  const { status, startRequest, handleSuccess, handleError } = useRequestState();
  const { setError } = useApiErrorContext();

  const handleFetchUserSports = useCallback(async () => {
    if (!user?.id || isGuestUser(user)) {
      return;
    }

    startRequest();

    const sports = await fetchCollection<SportType>({
      pathname: `${ApiRoute.USERS}/${child?.id || user.id}/${ApiRoute.SPORTS}`,
      revalidate: 0,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      unpaginate: true,
    });

    if (sports['hydra:description']) {
      handleError();
      return setError(sports);
    }

    setUserSports(sports);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const save = async (sports: SportType[]) => {
    startRequest();

    const postData = {
      ...sports && {
        sports: sports.map(sport => sport['@id']),
      },
      sportUpdatedAt: new Date(),
    };

    const path = isMainUser(user) ? 'users' : 'guest_users';
    const id = child?.id || user?.id;

    const response = await fetchItem<User>({
      pathname: `${path}/${id}`,
      method: 'PUT',
      body: postData,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
    });

    if (response['hydra:description']) {
      handleError();
      setError(response);
      throw new Error('Error updating user sports');
    }

    if (!child?.id && !isGuestUser(user)) {
      setUser(response);
    }

    setUserSports(actual => ({
      ...actual,
      'hydra:member': sports,
    }));

    handleSuccess();
  };

  useEffect(() => {
    handleFetchUserSports();
  }, [handleFetchUserSports]);

  return {
    isFetching: status === RequestStatus.PENDING,
    userSports,
    save,
  };
}
